import { useState, useEffect } from "react";
import {
  getDashboardData,
  getDashboardDataWithRange,
  getDashboardReport,
} from "../../../services/admin/adminServices";

export default function Dashboard() {
  const [isDashboardData, setDashboardData] = useState();
  const [period, setPeriod] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rangeData, setRangeData] = useState();
  const [rangeLoading, setRangeLoading] = useState(false);
  const [isDashboardReportData, setDashboardReportData] = useState();

  const PERIOD_MAP = {
    today: "today",
    yesterday: "last_day",
    "7d": "last_7day",
    week: "last_week",
    month: "this_month",
    lmonth: "last_month",
    custom: "custom",
  };

  useEffect(() => {
    fetchDashboardData();
    fetchDashboardReportDetail();
  }, []);

  useEffect(() => {
    if (period !== "custom") {
      fetchRangeData();
    }
  }, [period]);

  const fetchDashboardData = async () => {
    try {
      const res = await getDashboardData();
      if (res?.statusCode === 1) {
        setDashboardData(res?.data);
      }
    } catch (err) {
      console.log(
        err.response?.data?.message || "Failed to fetch dashboard data",
        "error",
      );
    }
  };

  const recentSignUp = isDashboardData?.recentSignups || [];

  const expiringSoon = isDashboardData?.expiringSoon || [];

  const getExpiryBadge = (expiresOn) => {
    const today = new Date();
    const expiry = new Date(expiresOn);
    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0)
      return <span className="badge badge-danger">Expired</span>;
    if (daysLeft <= 7)
      return <span className="badge badge-warn">Expiring in {daysLeft} d</span>;
    if (daysLeft <= 30)
      return <span className="badge badge-due">Expiring in {daysLeft} d</span>;
    return <span className="badge badge-active">Active</span>;
  };

  const fetchRangeData = async (customFrom = null, customTo = null) => {
    setRangeLoading(true);
    try {
      const res = await getDashboardDataWithRange(
        PERIOD_MAP[period],
        customFrom,
        customTo,
      );
      if (res.statusCode === 1) {
        setRangeData(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRangeLoading(false);
    }
  };

  const handleApplyCustom = () => {
    if (!fromDate || !toDate) return;
    // convert "2026-05-08" (input date format) → "08 May 2026" (API format)
    const fmt = (d) =>
      new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    fetchRangeData(fmt(fromDate), fmt(toDate));
  };

  const fetchDashboardReportDetail = async () => {
    try {
      const res = await getDashboardReport();
      if (res?.statusCode === 1) {
        setDashboardReportData(res?.data);
      }
    } catch (err) {
      console.log(
        err.response?.data?.message || "Failed to fetch dashboard Report Data",
        "error",
      );
    }
  };
  const revenueByPeriod = isDashboardReportData?.revenueByPeriod || [];
  const packageDistribution = isDashboardReportData?.packageDistribution || [];

  // For progress bar width — find max revenue to calculate relative %
  const maxRevenue = Math.max(...revenueByPeriod.map((r) => r.revenue), 1);
  const maxSubscriptions = Math.max(
    ...packageDistribution.map((p) => p.totalSubscriptions),
    1,
  );

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Dashboard
            </div>
            <div className="page-sub" id="page-sub">
              SaaS platform overview
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>
        <div className="content" id="content-area">
          <div className="stat-grid" style={{ marginBottom: 12 }}>
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-label">Total Users</div>
              <div className="stat-value">{isDashboardData?.totalUsers}</div>
              <div className="stat-delta delta-neu">All registered owners</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Active Subscribers</div>
              <div className="stat-value">
                {isDashboardData?.activeSubscriptions}
              </div>
              <div className="stat-delta delta-up">With valid subscription</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⛔</div>
              <div className="stat-label">Expired Subscribers</div>
              <div className="stat-value">
                {isDashboardData?.expiredSubscriptions}
              </div>
              <div className="stat-delta delta-dn">Subscription ended</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⚠️</div>
              <div className="stat-label">Expiring in 7 Days</div>
              <div className="stat-value">
                {isDashboardData?.expiringIn7Days}
              </div>
              <div className="stat-delta delta-dn">Need renewal soon</div>
            </div>
          </div>

          <div className="stat-grid" style={{ marginBottom: 16 }}>
            <div
              className="stat-card"
              style={{ cursor: "pointer", border: "1px solid #FCD34D" }}
            >
              <div className="stat-icon">⏳</div>
              <div className="stat-label">Owner KYC Pending</div>
              <div className="stat-value" style={{ color: "#92400E" }}>
                {isDashboardData?.pendingKyc}
              </div>
              <div className="stat-delta delta-dn">
                Awaiting verification · click to view
              </div>
            </div>
            <div
              className="stat-card"
              style={{ cursor: "pointer", border: "1px solid #BFDBFE" }}
            >
              <div className="stat-icon">📥</div>
              <div className="stat-label">Fund Requests</div>
              <div className="stat-value" style={{ color: "#185FA5" }}>
                {isDashboardData?.pendingFundRequests}
              </div>
              <div className="stat-delta delta-dn">
                ₹ {isDashboardData?.pendingFundRequestAmount} pending · click to
                view
              </div>
            </div>
          </div>

          <div className="date-filter-bar">
            <label>Showing period data for:</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="today">Today</option>
              <option value="yesterday">Last Day</option>
              <option value="7d">Last 7 Days</option>
              <option value="week">Last Week</option>
              <option value="month">This Month</option>
              <option value="lmonth">Last Month</option>
              <option value="custom">Custom Date</option>
            </select>

            {/* Custom date inputs — only show when custom selected */}
            {period === "custom" && (
              <div className="flex-gap8">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  to
                </span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleApplyCustom}
                  disabled={!fromDate || !toDate}
                >
                  Apply
                </button>
              </div>
            )}

            <span
              style={{
                fontSize: 11,
                color: "var(--text-tertiary)",
                marginLeft: 4,
              }}
            >
              {rangeLoading ? "Loading..." : ""}
            </span>
          </div>

          <div className="stat-grid-5" id="period-stats">
            <div className="stat-card">
              <div className="stat-icon">🆕</div>
              <div className="stat-label">New Users</div>
              <div className="stat-value">{rangeData?.newUsers}</div>
              <div className="stat-delta delta-up">Registered in period</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💳</div>
              <div className="stat-label">New Subscriptions</div>
              <div className="stat-value">{rangeData?.newSubscriptions}</div>
              <div className="stat-delta delta-up">First-time purchases</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔄</div>
              <div className="stat-label">Renewals</div>
              <div className="stat-value">{rangeData?.renewals}</div>
              <div className="stat-delta delta-up">Subscription renewals</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">❌</div>
              <div className="stat-label">Expired</div>
              <div className="stat-value">{rangeData?.expiredInPeriod}</div>
              <div className="stat-delta delta-dn">Lapsed this period</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-label">Revenue</div>
              <div className="stat-value">₹ {rangeData?.revenueInPeriod}</div>
              <div className="stat-delta delta-up">Total collections</div>
            </div>
          </div>
          <div className="two-col">
            <div className="card">
              <div className="card-title">Recent Signups</div>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSignUp.map((user) => (
                    <tr key={user?.userId}>
                      <td>{user?.fullName}</td>
                      <td>{user?.mobile}</td>
                      <td>
                        {new Date(user.registeredOn).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                          },
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            user?.subscriptionStatus === "Active"
                              ? "badge-active"
                              : user?.subscriptionStatus === "Expiring"
                                ? "badge-due"
                                : user?.subscriptionStatus === "Expired"
                                  ? "badge-danger"
                                  : "badge-warn"
                          }`}
                        >
                          {user?.subscriptionStatus || "None"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card">
              <div className="card-title">Expiring Soon </div>
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Package</th>
                    <th>Expires</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {expiringSoon.map((item) => (
                    <tr key={item?.ownerId}>
                      <td>{item?.ownerName}</td>
                      <td>{item?.packageName}</td>
                      <td>
                        {new Date(item?.expiresOn).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        })}
                      </td>
                      <td>{getExpiryBadge(item.expiresOn)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="two-col">
            {/* Revenue Trend */}
            <div className="card">
              <div className="card-title">Revenue Trend</div>
              {revenueByPeriod?.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
                  No data available.
                </div>
              ) : (
                revenueByPeriod?.map((item) => (
                  <div key={item?.periodLabel} style={{ marginBottom: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        marginBottom: 3,
                      }}
                    >
                      <span>{item?.periodLabel}</span>
                      <span style={{ fontWeight: 600 }}>
                        ₹ {(item?.revenue / 100000).toFixed(1)}L
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.round((item.revenue / maxRevenue) * 100)}%`,
                          background: "#378ADD",
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Package Distribution */}
            <div className="card">
              <div className="card-title">Package Distribution</div>
              {packageDistribution?.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
                  No data available.
                </div>
              ) : (
                packageDistribution?.map((pkg, index) => {
                  const colors = [
                    "#639922",
                    "#185FA5",
                    "#534AB7",
                    "#0F6E56",
                    "#378ADD",
                  ];
                  const pct = Math.round(
                    (pkg?.totalSubscriptions / maxSubscriptions) * 100,
                  );
                  return (
                    <div key={pkg?.packageId} style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          marginBottom: 3,
                        }}
                      >
                        <span>{pkg?.packageName}</span>
                        <span style={{ color: "var(--text-secondary)" }}>
                          {pkg?.totalSubscriptions} users · {pct}%
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${pct}%`,
                            background: colors[index % colors.length],
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
